import React, { useContext, useState, useEffect, useRef } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Modal, Form, Card, Button } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import axios from "axios";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import Swal from "sweetalert2";
import { FiCheck, FiEdit2, FiTrash2 } from "react-icons/fi";

const normalizeAttributes = (attributes) => {
    if (Array.isArray(attributes)) {
        return attributes.map((attribute) => ({
            name: String(attribute?.name ?? ""),
            value: String(attribute?.value ?? ""),
            isCustom: false,
        }));
    }

    if (attributes && typeof attributes === "object") {
        return Object.entries(attributes).map(([name, value]) => ({
            name: String(value && typeof value === "object" ? value.name ?? name : name),
            value: String(value && typeof value === "object" ? value.value ?? "" : value ?? ""),
            isCustom: false,
        }));
    }

    return [];
};

const decodeHtmlEntity = (html) => {
    if (!html) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    let decoded = textarea.value;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    return decoded;
};

const decodeDescription = (html) => {
    if (!html) return "";
    let decoded = decodeHtmlEntity(html);
    decoded = decoded.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, "$1");
    return decoded.trim();
};

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.style.padding = "16px 20px";
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
    }
});

export default function CheckerProductView() {
    const navigate = useNavigate();
    const params = useParams();
    const productId = params.id || params.product;

    const { t } = useContext(TranslatorContext);

    const [productData, setProductData] = useState(null);
    const [selectedImage, setSelectedImage] = useState("");
    const [loading, setLoading] = useState(true);

    const [showReasonModal, setShowReasonModal] = useState(false);
    const [actionType, setActionType] = useState("");
    const [actionReason, setActionReason] = useState("");
    const [submittingDecision, setSubmittingDecision] = useState(false);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [attributeValues, setAttributeValues] = useState([]);
    const [editingAttributeIndex, setEditingAttributeIndex] = useState(null);
    const skipInitialCategoryAttributeFetch = useRef(false);

    const [editingField, setEditingField] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        category_id: "",
        brand: "",
        selling_style: "Fixed",
        sell_price_dollar: 0,
        auction_min_price: "",
        shipping_charges_dollar: 0,
        product_qty: 0,
        short_description: "",
        description: ""
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const authHeader = {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                };

                const initRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/checker/products/initial-data`,
                    authHeader
                );
                if (initRes.data) {
                    const resData = initRes.data.data || initRes.data;
                    setCategories(resData.categories || []);
                    setBrands(resData.brands || []);
                }

                const prodRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/checker/product/${productId}`,
                    authHeader
                );

                const item = prodRes.data?.data || prodRes.data;

                if (item) {
                    setProductData(item);
                    setSelectedImage(item?.card_img || "");
                    const productAttributes = normalizeAttributes(item?.attributes);
                    skipInitialCategoryAttributeFetch.current = productAttributes.length > 0;
                    setAttributeValues(productAttributes);

                    setFormData({
                        title: item?.title || "",
                        category_id: item?.category_id || item?.category?.id || "",
                        brand: item?.brand?.id || item?.brand || "",
                        selling_style: item?.selling_style || "Fixed",
                        sell_price_dollar: item?.sell_price_dollar ? Math.round(Number(item.sell_price_dollar)) : 0,
                        auction_min_price: item?.auction_min_price ?? item?.pricing?.auction_min_price ?? "",
                        shipping_charges_dollar: item?.shipping_charges_dollar || 0,
                        product_qty: item?.product_qty || 0,
                        short_description: decodeDescription(item?.short_description),
                        description: decodeDescription(item?.description)
                    });
                }
            } catch (error) {
                console.error("Failed to fetch product data", error);
                Toast.fire({
                    icon: "error",
                    title: "Failed to load product details"
                });
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        if (!formData.category_id) {
            return undefined;
        }

        if (skipInitialCategoryAttributeFetch.current) {
            skipInitialCategoryAttributeFetch.current = false;
            return undefined;
        }

        let isCurrentCategory = true;
        const fetchCategoryAttributes = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/categories/${formData.category_id}/attributes`,
                    {
                        headers: {
                            Authorization: `******"authToken")}`,
                        },
                    }
                );
                if (!isCurrentCategory) {
                    return;
                }

                const categoryAttributes = (response.data.attributes || [])
                    .map((attribute) => typeof attribute === "string" ? attribute : attribute?.name)
                    .filter((name) => typeof name === "string" && name.trim());

                setAttributeValues((currentAttributes) => {
                    const existingByName = new Map(
                        currentAttributes
                            .filter((attribute) => attribute.name.trim())
                            .map((attribute) => [attribute.name.trim().toLowerCase(), attribute])
                    );
                    return categoryAttributes.map((name) => ({
                        name,
                        value: existingByName.get(name.trim().toLowerCase())?.value || "",
                        isCustom: false,
                    }));
                });
                setEditingAttributeIndex(null);
            } catch (error) {
                if (isCurrentCategory) {
                    console.error("Failed to fetch category attributes:", error);
                    Toast.fire({
                        icon: "error",
                        title: error.response?.data?.message || "Failed to load category attributes",
                    });
                }
            }
        };

        fetchCategoryAttributes();
        return () => {
            isCurrentCategory = false;
        };
    }, [formData.category_id]);

    const handleDeleteImage = (imageId) => {
        Swal.fire({
            title: "Delete Image?",
            text: "Are you sure you want to delete this image?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(
                        `${process.env.REACT_APP_API_URL}/checker/delete-image/${imageId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("authToken")}`
                            }
                        }
                    );

                    const updatedImages = productData?.images?.filter((img) => img.id !== imageId);
                    setProductData((prev) => ({
                        ...prev,
                        images: updatedImages
                    }));

                    const deletedImgObj = productData?.images?.find((img) => img.id === imageId);
                    if (selectedImage === deletedImgObj?.image) {
                        setSelectedImage(productData?.card_img || "");
                    }

                    Toast.fire({
                        icon: "success",
                        title: "Image deleted successfully!"
                    });
                } catch (error) {
                    console.error("Failed to delete image", error);
                    Toast.fire({
                        icon: "error",
                        title: error?.response?.data?.message || "Failed to delete image"
                    });
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "sell_price_dollar") {
            const parsedInt = parseInt(value, 10);
            setFormData((prev) => ({
                ...prev,
                [name]: isNaN(parsedInt) ? "" : parsedInt
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAttributeChange = (indexToUpdate, changes) => {
        setAttributeValues((prev) => prev.map((attribute, index) => (
            index === indexToUpdate ? { ...attribute, ...changes } : attribute
        )));
    };

    const addAttribute = () => {
        setEditingAttributeIndex(attributeValues.length);
        setAttributeValues((prev) => [...prev, { name: "", value: "", isCustom: true }]);
    };

    const removeAttribute = (indexToRemove) => {
        setAttributeValues((prev) => prev.filter((_, index) => index !== indexToRemove));
        if (editingAttributeIndex === indexToRemove) {
            setEditingAttributeIndex(null);
        } else if (editingAttributeIndex > indexToRemove) {
            setEditingAttributeIndex(editingAttributeIndex - 1);
        }
    };

    const copyAttributeValue = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            Toast.fire({ icon: "success", title: "Attribute value copied" });
        } catch (error) {
            console.error("Failed to copy attribute value:", error);
            Toast.fire({ icon: "error", title: "Could not copy attribute value" });
        }
    };

    const handleShortDescriptionChange = (content) => {
        setFormData((prev) => ({
            ...prev,
            short_description: content
        }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({
            ...prev,
            description: content
        }));
    };

    const submitDecision = async (decisionType, reason = "") => {
        try {
            setSubmittingDecision(true);
            const payload = new FormData();

            payload.append("type", decisionType);
            payload.append("reason", reason);
            payload.append("category_id", formData.category_id);
            payload.append("brand", formData.brand);
            payload.append("selling_style", formData.selling_style);
            payload.append("title", formData.title);
            payload.append("description", formData.description);
            payload.append("short_description", formData.short_description);
            payload.append("sell_price_dollar", Math.round(Number(formData.sell_price_dollar) || 0));
            if (formData.selling_style === "Auction") {
                payload.append("auction_min_price", formData.auction_min_price);
            }
            payload.append("shipping_charges_dollar", formData.shipping_charges_dollar);
            payload.append("product_qty", formData.product_qty);

            attributeValues.forEach((attribute, index) => {
                payload.append(`attributes[${index}][name]`, attribute.name);
                payload.append(`attributes[${index}][value]`, attribute.value);
            });

            payload.append("_method", "PUT");

            await axios.post(
                `${process.env.REACT_APP_API_URL}/checker/products/${productData?.id || productId}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            let successMsg = "Product updated successfully!";
            if (decisionType === "approve") successMsg = "Product approved successfully!";
            if (decisionType === "reject") successMsg = "Product rejected successfully!";
            if (decisionType === "dismiss") successMsg = "Product dismissed successfully!";

            Toast.fire({
                icon: "success",
                title: successMsg
            });

            if (decisionType === "reject" || decisionType === "dismiss") {
                setShowReasonModal(false);
                setActionReason("");
                setActionType("");
            }

            navigate("/checker-products?tab=recieved");
        } catch (error) {
            console.error(error);
            Toast.fire({
                icon: "error",
                title: error?.response?.data?.message || `Unable to ${decisionType} product`
            });
        } finally {
            setSubmittingDecision(false);
        }
    };

    const handleApprove = () => {
        Swal.fire({
            title: "Approve Product?",
            text: "Are you sure you want to approve this product?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#198754",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Approve!"
        }).then((result) => {
            if (result.isConfirmed) {
                submitDecision("approve", "");
            }
        });
    };

    const handleOpenModal = (type) => {
        setActionType(type);
        setShowReasonModal(true);
    };

    const handleReasonSubmit = () => {
        if (!actionReason.trim() && actionType === "reject") {
            Toast.fire({
                icon: "warning",
                title: "Please enter rejection reason"
            });
            return;
        }
        submitDecision(actionType, actionReason.trim());
    };

    const editorButtonList = [
        ['undo', 'redo'],
        ['font', 'fontSize', 'formatBlock'],
        ['paragraphStyle', 'blockquote'],
        ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
        ['fontColor', 'hiliteColor', 'textStyle'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list', 'lineHeight'],
        ['table', 'link', 'image', 'video'],
        ['fullScreen', 'showBlocks', 'codeView'],
        ['preview', 'print']
    ];

    const renderEditableValue = (fieldKey, displayValue, type = "text", options = [], step = "any", datalistId = null) => {
        const isEditing = editingField === fieldKey;

        return (
            <div className="d-flex align-items-center flex-grow-1 flex-wrap gap-2 min-w-0">
                {!isEditing ? (
                    fieldKey === "short_description" ? (
                        <div
                            className="mb-0 text-break text-dark fw-medium flex-grow-1"
                            dangerouslySetInnerHTML={{ __html: formData.short_description }}
                        />
                    ) : (
                        <p className="mb-0 text-break text-dark fw-medium flex-grow-1">{displayValue}</p>
                    )
                ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                        {type === "select" ? (
                            <Form.Select
                                size="sm"
                                name={fieldKey}
                                value={formData[fieldKey]}
                                onChange={handleInputChange}
                                className="w-100"
                            >
                                <option value="">Select Option</option>
                                {options.map((opt) => (
                                    <option key={opt.id || opt.value} value={opt.id || opt.value}>
                                        {opt.name || opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : type === "datalist" ? (
                            <>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    list={datalistId}
                                    name={fieldKey}
                                    value={formData[fieldKey]}
                                    onChange={handleInputChange}
                                    className="w-100"
                                    placeholder="Select or type..."
                                />
                                <datalist id={datalistId}>
                                    {options.map((item, index) => (
                                        <option key={index} value={typeof item === 'object' ? item.name : item} />
                                    ))}
                                </datalist>
                            </>
                        ) : fieldKey === "short_description" ? (
                            <div className="w-100 bg-white rounded">
                                <SunEditor
                                    lang="en"
                                    placeholder="Write short product description..."
                                    setContents={formData.short_description}
                                    onChange={handleShortDescriptionChange}
                                    setOptions={{
                                        buttonList: editorButtonList,
                                        height: 200,
                                    }}
                                />
                            </div>
                        ) : type === "textarea" ? (
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name={fieldKey}
                                value={formData[fieldKey]}
                                onChange={handleInputChange}
                                className="w-100"
                            />
                        ) : (
                            <Form.Control
                                size="sm"
                                type={type}
                                step={step}
                                name={fieldKey}
                                value={formData[fieldKey]}
                                onChange={handleInputChange}
                                className="w-100"
                            />
                        )}
                    </div>
                )}

                <i
                    className={`material-icons cursor-pointer ${isEditing ? "text-success" : "text-primary ms-auto"}`}
                    onClick={() => setEditingField(isEditing ? null : fieldKey)}
                    style={{ cursor: "pointer", fontSize: "20px" }}
                >
                    {isEditing ? "check" : "edit"}
                </i>
            </div>
        );
    };

    const getSelectedName = (list, id) => {
        const found = list.find((item) => String(item.id) === String(id));
        return found ? found.name : id;
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <h5>Loading product details...</h5>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb">
                    <h3 className="mc-breadcrumb-title">{t("product_view")}</h3>
                    <ul className="mc-breadcrumb-list">
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">
                            <Link to="#" className="mc-breadcrumb-link">{t("products")}</Link>
                        </li>
                        <li className="mc-breadcrumb-item">{t("product_view")}</li>
                    </ul>
                </div>
            </div>

            <div className="mc-card p-3 p-md-4">
                <Row className="g-4">
                    <Col lg={5} xl={5}>
                        <h6 className="mc-divide-title mb-3">{productData?.product_code}</h6>
                        <div className="mc-product-view-gallery">
                            <div className="mb-3 text-center border rounded p-2 bg-light">
                                <img
                                    src={process.env.REACT_APP_IMG_URL + selectedImage}
                                    alt="product"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: "350px", objectFit: "contain", width: "100%" }}
                                />
                            </div>
                            <div className="d-flex flex-wrap gap-2 justify-content-center">
                                <div className="position-relative">
                                    <img
                                        src={process.env.REACT_APP_IMG_URL + productData?.card_img}
                                        alt="card-main"
                                        onClick={() => setSelectedImage(productData?.card_img)}
                                        className={`img-thumbnail cursor-pointer ${selectedImage === productData?.card_img ? "border-primary border-2" : ""}`}
                                        style={{ width: "65px", height: "65px", objectFit: "cover", cursor: "pointer" }}
                                    />
                                </div>

                                {productData?.images?.map((img) => (
                                    <div key={img.id} className="position-relative">
                                        <img
                                            src={process.env.REACT_APP_IMG_URL + img.image}
                                            alt="product-sub"
                                            onClick={() => setSelectedImage(img.image)}
                                            className={`img-thumbnail cursor-pointer ${selectedImage === img.image ? "border-primary border-2" : ""}`}
                                            style={{ width: "65px", height: "65px", objectFit: "cover", cursor: "pointer" }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm p-0 position-absolute d-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                top: "-6px",
                                                right: "-6px",
                                                width: "20px",
                                                height: "20px",
                                                fontSize: "12px",
                                                zIndex: 2
                                            }}
                                            title="Delete Image"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteImage(img.id);
                                            }}
                                        >
                                            <i className="material-icons" style={{ fontSize: "14px" }}>delete</i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>

                    <Col lg={7} xl={7}>
                        <h6 className="mc-divide-title mb-4">{t("product_details")}</h6>
                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex align-items-center gap-2 pb-2 border-bottom">
                                <h4 className="m-0 flex-grow-1 text-break">{formData.title}</h4>
                                {renderEditableValue("title", "", "text")}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">inventory_2</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Product Code</h6>
                                <span className="me-2">:</span>
                                <p className="mb-0 text-break">{productData?.product_code}</p>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">category</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Category</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("category_id", getSelectedName(categories, formData.category_id), "select", categories)}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">branding_watermark</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Brand</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("brand", getSelectedName(brands, formData.brand), "select", brands)}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">sell</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Selling Style</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("selling_style", formData.selling_style, "select", [
                                    { value: "Fixed", label: "Fixed" },
                                    { value: "Auction", label: "Auction" }
                                ])}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">attach_money</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Selling Price $</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("sell_price_dollar", `$${formData.sell_price_dollar || 0}`, "number", [], "1")}
                            </div>

                            {formData.selling_style === "Auction" && (
                                <div className="d-flex align-items-center gap-2">
                                    <i className="material-icons text-muted">gavel</i>
                                    <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Auction Min Price $</h6>
                                    <span className="me-2">:</span>
                                    {renderEditableValue(
                                        "auction_min_price",
                                        formData.auction_min_price === "" ? "N/A" : `$${formData.auction_min_price}`,
                                        "number",
                                        [],
                                        "0.01"
                                    )}
                                </div>
                            )}

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">local_shipping</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Shipping Charges $</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("shipping_charges_dollar", `$${formData.shipping_charges_dollar}`, "number")}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <i className="material-icons text-muted">confirmation_number</i>
                                <h6 className="m-0 text-nowrap" style={{ minWidth: "140px" }}>Quantity</h6>
                                <span className="me-2">:</span>
                                {renderEditableValue("product_qty", formData.product_qty, "number")}
                            </div>

                            <div className="d-flex align-items-start gap-2">
                                <i className="material-icons text-muted mt-1">description</i>
                                <h6 className="m-0 text-nowrap mt-1" style={{ minWidth: "140px" }}>Short Description</h6>
                                <span className="me-2 mt-1">:</span>
                                {renderEditableValue("short_description", formData.short_description, "textarea")}
                            </div>

                            <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top">
                                <button
                                    type="button"
                                    className="btn btn-success d-flex align-items-center gap-1 px-4"
                                    onClick={handleApprove}
                                    disabled={submittingDecision}
                                >
                                    <i className="material-icons">check</i>
                                    Approve
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger d-flex align-items-center gap-1 px-4"
                                    onClick={() => handleOpenModal("reject")}
                                    disabled={submittingDecision}
                                >
                                    <i className="material-icons">close</i>
                                    Reject
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-warning text-white d-flex align-items-center gap-1 px-4"
                                    onClick={() => handleOpenModal("dismiss")}
                                    disabled={submittingDecision}
                                >
                                    <i className="material-icons">block</i>
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </Col>

                    <Col xs={12}>
                        <Card className="shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Product Attributes</h5>
                                <Button type="button" variant="primary" size="sm" onClick={addAttribute}>
                                    Add Attribute
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {attributeValues.length === 0 ? (
                                    <p className="text-center text-muted mb-0 py-3">No attributes added.</p>
                                ) : (
                                    <Row className="g-3">
                                        {attributeValues.map((attribute, index) => (
                                            <Col md={6} key={index}>
                                                <div className="h-100 border rounded p-3">
                                                    {editingAttributeIndex === index ? (
                                                        <Row className="g-2 align-items-center">
                                                            <Col sm={5}>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="text"
                                                                    value={attribute.name}
                                                                    onChange={(e) => handleAttributeChange(index, { name: e.target.value })}
                                                                    placeholder="Attribute name"
                                                                    aria-label="Attribute name"
                                                                />
                                                            </Col>
                                                            <Col sm={5}>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="text"
                                                                    value={attribute.value}
                                                                    onChange={(e) => handleAttributeChange(index, { value: e.target.value })}
                                                                    placeholder="Value"
                                                                    aria-label="Attribute value"
                                                                />
                                                            </Col>
                                                            <Col sm={2} className="text-end">
                                                                <Button
                                                                    type="button"
                                                                    variant="success"
                                                                    size="sm"
                                                                    className="d-inline-flex align-items-center justify-content-center"
                                                                    style={{ width: 34, height: 34, padding: 0 }}
                                                                    aria-label="Finish editing attribute"
                                                                    title="Done"
                                                                    onClick={() => setEditingAttributeIndex(null)}
                                                                >
                                                                    <FiCheck size={16} />
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="flex-grow-1 min-w-0">
                                                                <div className="d-flex flex-wrap align-items-baseline gap-2">
                                                                    <span className="fw-semibold text-break">{attribute.name || "Unnamed attribute"}:</span>
                                                                    <span className="fw-semibold text-break">{attribute.value || "—"}</span>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex flex-shrink-0 gap-1">
                                                                <Button
                                                                    type="button"
                                                                    variant="secondary"
                                                                    size="sm"
                                                                    className="d-inline-flex align-items-center justify-content-center"
                                                                    style={{ width: 34, height: 34, padding: 0 }}
                                                                    aria-label="Copy attribute value"
                                                                    title="Copy value"
                                                                    onClick={() => copyAttributeValue(attribute.value)}
                                                                    disabled={!attribute.value}
                                                                >
                                                                    <i className="material-icons" aria-hidden="true" style={{ fontSize: 18 }}>content_copy</i>
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="primary"
                                                                    size="sm"
                                                                    className="d-inline-flex align-items-center justify-content-center"
                                                                    style={{ width: 34, height: 34, padding: 0 }}
                                                                    aria-label="Edit attribute"
                                                                    title="Edit"
                                                                    onClick={() => setEditingAttributeIndex(index)}
                                                                >
                                                                    <FiEdit2 size={15} />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="d-inline-flex align-items-center justify-content-center"
                                                                    style={{ width: 34, height: 34, padding: 0 }}
                                                                    aria-label="Remove attribute"
                                                                    title="Remove"
                                                                    onClick={() => removeAttribute(index)}
                                                                >
                                                                    <FiTrash2 size={15} />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12}>
                        <div className="mt-4 pt-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mc-divide-title mb-0">{t("product_description")}</h6>
                            </div>
                            <div className="mc-product-description bg-white rounded">
                                <SunEditor
                                    lang="en"
                                    placeholder="Write complete product description..."
                                    setContents={formData.description}
                                    onChange={handleDescriptionChange}
                                    setOptions={{
                                        buttonList: editorButtonList,
                                        height: 350,
                                    }}
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>

            <Modal
                show={showReasonModal}
                onHide={() => {
                    if (!submittingDecision) {
                        setShowReasonModal(false);
                        setActionType("");
                        setActionReason("");
                    }
                }}
                centered
            >
                <Modal.Header closeButton={!submittingDecision} className="px-4 pt-4 pb-2 border-0">
                    <Modal.Title className="text-capitalize">{actionType} Product</Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4 py-2">
                    <div className="mb-2">
                        <label className="form-label fw-semibold">
                            {actionType === "reject" ? "Rejection Reason" : "Reason (Optional)"}
                        </label>
                        <textarea
                            className="form-control"
                            rows={4}
                            placeholder={
                                actionType === "reject"
                                    ? "Enter reason for rejecting this product..."
                                    : "Enter reason for dismissing this product..."
                            }
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            disabled={submittingDecision}
                        />
                    </div>
                </Modal.Body>

                <Modal.Footer className="px-4 pb-4 pt-2 border-0">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            setShowReasonModal(false);
                            setActionType("");
                            setActionReason("");
                        }}
                        disabled={submittingDecision}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className={`btn d-flex align-items-center gap-1 ${actionType === "reject" ? "btn-danger" : "btn-warning text-white"}`}
                        onClick={handleReasonSubmit}
                        disabled={submittingDecision || (actionType === "reject" && !actionReason.trim())}
                    >
                        {submittingDecision ? (
                            "Processing..."
                        ) : (
                            <>
                                <i className="material-icons fs-6">{actionType === "reject" ? "close" : "block"}</i>
                                <span className="text-capitalize">{actionType} Product</span>
                            </>
                        )}
                    </button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}